import "./DataItemForm.less";

import { Button, Collapse, Form, Icon, Input, Popover, Select, Tooltip } from "antd";
import { FormComponentProps } from "antd/lib/form";
import { OptionProps } from "antd/lib/select";
import { Guid } from "guid-typescript";
import { cloneDeep, isEmpty } from "lodash";
import { curry, __ } from "lodash/fp";
import React from "react";
import MaskedInput from "react-text-mask";

import { IBaseProps, IForm } from "lib/data/interfaces";
import {
	AggregateDataField,
	AggregateDataFieldValue,
	DataFieldTemplate,
	DataFieldValue,
	ImageDataField,
	ImageDataFieldValue,
	LinkDataField,
	LinkDataFieldValue,
	ListDataField,
	ListDataFieldValue,
	OrderedDataFieldValue,
	SingleItemRequirement,
	StringDataField,
	StringDataFieldValue,
	TextDataField,
	TextDataFieldValue
} from "lib/data/model";
import { Requiredness } from "lib/data/model/enumerations";
import { MultiField } from "./lib/MultiField";
import { UniField } from "./lib/UniField";


interface IDataItemFormProps extends IBaseProps {
	template: DataFieldTemplate;
	value?: DataFieldValue;
	isRequired?: boolean;
	onValidated?: (isValid: boolean) => void;
}

interface IDataItemFormState {
	isLinkFieldLabelCollapsed: boolean;
}

class DataItemFormDefinition extends React.Component<IDataItemFormProps & FormComponentProps, IDataItemFormState> implements IForm<DataFieldValue> {

	public static defaultProps = {
		isRequired: true
	};

	private value!: DataFieldValue;
	private fields: Array<UniField | MultiField> = [];

	public constructor(props: IDataItemFormProps & FormComponentProps) {

		super(props);

		this.state = {
			isLinkFieldLabelCollapsed: true
		}
	}

	public componentWillMount = () => {

		// Initialize Form
		this.initialize(this.props.template, this.props.value);
	}

	public componentWillReceiveProps = (newProps: IDataItemFormProps & FormComponentProps) => {

		// Check Passed In Value
		if (newProps.value !== this.props.value) {

			// Clear Any Previous Fields
			this.fields = [];

			// Initialize Form
			this.initialize(newProps.template, newProps.value);
		}
	}

	public save = (): DataFieldValue | undefined => {

		// Initialize Return Value
		let returnValue: DataFieldValue | undefined;

		this.props.form.validateFields({ force: true }, (errors, values) => {

			if (errors === undefined
				|| errors === null
				|| errors.length === 0) {

				// Loop Through Provided Values
				for (const fieldName of Object.keys(values)) {

					// Check For "Other" Value
					if (fieldName === "link-destination") {

						// Set Destination On Root Value
						(this.value as LinkDataFieldValue).destination = values[fieldName];
					}
					else if (fieldName === "link-protocol") {

						// Get Protocol ID
						const id = values[fieldName];

						// Check Existing Selected Protocol
						if ((this.value as LinkDataFieldValue).protocol === undefined || (this.value as LinkDataFieldValue).protocol!.id !== id) {

							// Set Destination On Root Value
							(this.value as LinkDataFieldValue).protocol = ((this.value as LinkDataFieldValue).template.field as LinkDataField).availableProtocols!.find((protocol) => protocol.id === id);
						}
					}
					else {

						// Find Matching Data Field Value
						const foundValue = this.flattenFields(this.fields).find((i) => i.name === fieldName);

						// Check For Matching Data Field Value
						if (foundValue && foundValue.value) {

							// Switch On Matching Data Field Type
							switch (foundValue.value.$type) {

								case StringDataFieldValue.$type:

									// Get Cast Data Field Value
									const stringDataFieldValue: StringDataFieldValue = foundValue.value as StringDataFieldValue;

									// Get String Value
									const stringValue = values[fieldName];
									console.log(stringValue);
									// Check For Link
									if (LinkDataField.isLinkField(stringDataFieldValue.template.field) && (stringDataFieldValue.template.field as LinkDataField).hasLink) {

										// Check String Value
										if (stringValue === undefined || stringValue === "") {

											// Set Value To Destination
											stringDataFieldValue.value = values["link-destination"];

											// Exit Switch
											break;
										}
									}

									// Update Value
									stringDataFieldValue.value = stringValue;

									break;

								// case ImageDataFieldValue.$type:

								// 	// Get Cast Data Field Value
								// 	const imageDataFieldValue: ImageDataFieldValue = foundValue.value as ImageDataFieldValue;

								// 	// Update Value
								// 	imageDataFieldValue.value = values[fieldName];

								// 	break;

								case ListDataFieldValue.$type:

									// Get Cast Data Field Value
									const listDataFieldValue: ListDataFieldValue = foundValue.value as ListDataFieldValue;

									// Get Selected Option From Field
									const option = (listDataFieldValue.template.field as ListDataField).options.find((i) => i.id === values[fieldName]);

									// Check Option
									if (option !== undefined) {

										// Update Value
										listDataFieldValue.value = option;
									}

									break;

								case TextDataFieldValue.$type:

									// Get Cast Data Field Value
									const textDataFieldValue: TextDataFieldValue = foundValue.value as TextDataFieldValue;

									// Update Value
									textDataFieldValue.value = values[fieldName];

									break;
							}
						}
					}
				}

				// Set Root Value As Return Value
				returnValue = this.value;
			}
		});

		// Return Value
		return returnValue;
	}

	public render() {

		// Check For Any Validation Errors
		this.checkValidation();

		return (

			<Form
				className="data-item-form"
				layout="vertical"
			>

				{this.fields.map((field) => (

					this.renderField(field)
				))}

			</Form>
		);
	}

	private renderField = (field: UniField | MultiField): React.ReactNode[] => {

		// Initialize Reat Nodes Array
		const nodes: React.ReactNode[] = [];

		// Check Field Type
		if (field instanceof UniField) {

			// Render Uni-Field & Add To Nodes
			nodes.push(this.renderUniField(field));
		}
		else if (field instanceof MultiField) {

			// Render Multi-Field & Add To Nodes
			nodes.push(this.renderMultiField(field));
		}

		// Return Nodes
		return nodes;
	}

	private renderUniField = (uniField: UniField): React.ReactNode[] => {

		// Initialize Nodes
		const nodes: React.ReactNode[] = [];

		// Set Field Error
		const error = this.props.form.isFieldTouched(`${uniField.name}`) && this.props.form.getFieldError(`${uniField.name}`);

		// Switch On Data Field Type
		switch (uniField.value.$type) {

			case StringDataFieldValue.$type:

				// Check For Link
				if (LinkDataField.isLinkField(uniField.value.template.field) && (uniField.value.template.field as LinkDataField).hasLink) {

					// Cast Field
					const linkField = uniField.value.template.field as LinkDataField;
					const linkValue = uniField.value as LinkDataFieldValue;

					// Set Field Error
					const linkDestinationError = this.props.form.isFieldTouched("link-destination") && this.props.form.getFieldError("link-destination");

					const isLinkFieldLabelCollapsed = isEmpty((uniField.value as StringDataFieldValue).value)
						|| (uniField.value as StringDataFieldValue).value === (uniField.value as StringDataFieldValue).destination;

					nodes.push(...[
						(
							<MaskedInput
								mask={this.processFieldMask(uniField.value.template.field.dataType.mask)}
								placeholder={`${uniField.value.template.field.name} Destination`}
								guide={false}
								key="link-destination"
								render={(reference, props) => {

									// Delete Invalid Props
									delete props.defaultValue;

									return (

										<Form.Item
											className="data-item-form-item data-item-link-item"
											validateStatus={(linkDestinationError ? "error" : undefined)}
											help={linkDestinationError || undefined}
										>
											{(
												this.props.form.getFieldDecorator("link-destination", {
													rules: (
														this.props.isRequired
															?
															[
																{
																	required: true,
																	message: `'${uniField.value.template.field.name}' is required`
																},
																{
																	pattern: new RegExp(String.raw`${uniField.value.template.field.dataType.format.pattern}`),
																	message: `Format Must Be: ${uniField.value.template.field.dataType.display}`
																}
															]
															:
															[
																{
																	pattern: new RegExp(String.raw`^$|${uniField.value.template.field.dataType.format.pattern}`),
																	message: `Format Must Be: ${uniField.value.template.field.dataType.display}`
																}
															]
													),
													initialValue: linkValue.destination
												})(
													<Input
														{...props}
														ref={(input) => (input !== null ? reference(input.input) : undefined)}
														addonBefore={(
															linkField.availableProtocols !== undefined && linkField.availableProtocols.length > 0
																?
																this.props.form.getFieldDecorator("link-protocol", {
																	rules: (
																		this.props.isRequired
																			?
																			[
																				{
																					required: true,
																					message: `'Protocol' is required`
																				}
																			]
																			:
																			[]
																	),
																	initialValue: linkValue.protocol ? linkValue.protocol.id : linkField.availableProtocols[0].id
																})(
																	<Select>
																		{linkField.availableProtocols.map((protocol, index) => <Select.Option key={index} value={protocol.id}>{protocol.name}</Select.Option>)}
																	</Select>
																)
																:
																undefined
														)}
														addonAfter={(
															<Popover
																title="Required Format"
																content={uniField.value.template.field.dataType.display}
															>
																<Icon type="info-circle" />
															</Popover>
														)}
													/>
												)
											)}
										</Form.Item>
									);
								}}
							/>
						),
						(
							<Collapse
								key={uniField.name}
								className="data-item-form-item data-item-link-item data-item-link-item-label-collapse"
								bordered={false}
								destroyInactivePanel={true}
								onChange={this.onLinkFieldLabelCollapse}
								activeKey={(
									!isLinkFieldLabelCollapsed || !this.state.isLinkFieldLabelCollapsed
										?
										["1"]
										:
										undefined
								)}
							>
								<Collapse.Panel
									key="1"
									header="Specify Label"
								>
									<Form.Item
										className="data-item-form-item"
										validateStatus={(error ? "error" : undefined)}
										help={error || undefined}
									>
										{(
											this.props.form.getFieldDecorator(`${uniField.name}`, {
												initialValue: ((uniField.value as StringDataFieldValue).value !== (uniField.value as StringDataFieldValue).destination ? (uniField.value as StringDataFieldValue).value : ""),
												rules: [
													(
														!isLinkFieldLabelCollapsed || !this.state.isLinkFieldLabelCollapsed
															?
															{
																required: true,
																message: "When expanded, 'Label' is required"
															}
															:
															{
																required: false
															}
													)
												],
												preserve: false
											})(
												<Input
													placeholder="Label"
												/>
											)
										)}
									</Form.Item>
								</Collapse.Panel>
							</Collapse>
						)
					]);
				}
				else {

					// Add Form Field
					nodes.push(
						<MaskedInput
							key={uniField.name}
							mask={this.processFieldMask(uniField.value.template.field.dataType.mask)}
							placeholder={uniField.value.template.field.name}
							guide={false}
							render={(reference, props) => {

								// Delete Invalid Props
								delete props.defaultValue;

								return (

									<Form.Item
										className="data-item-form-item"
										validateStatus={(error ? "error" : undefined)}
										help={error || undefined}
									>
										{(
											this.props.form.getFieldDecorator(`${uniField.name}`, {
												rules: (
													this.props.isRequired
														?
														[
															{
																required: true,
																message: `'${uniField.value.template.field.name}' is required`
															},
															{
																pattern: new RegExp(String.raw`${uniField.value.template.field.dataType.format.pattern}`),
																message: `Format Must Be: ${uniField.value.template.field.dataType.display}`
															}
														]
														:
														[
															{
																pattern: new RegExp(String.raw`^$|${uniField.value.template.field.dataType.format.pattern}`),
																message: `Format Must Be: ${uniField.value.template.field.dataType.display}`
															}
														]
												),
												initialValue: (uniField.value as StringDataFieldValue).value
											})(

												<Input
													{...props}
													ref={(input) => (input !== null ? reference(input.input) : undefined)}
													addonAfter={(
														<Popover
															title="Required Format"
															content={uniField.value.template.field.dataType.display}
														>
															<Icon type="info-circle" />
														</Popover>
													)}
												/>
											)
										)}
									</Form.Item>
								);
							}}
						/>
					);
				}

				break;

			case ListDataFieldValue.$type:

				// Get Value
				const listValue = uniField.value as ListDataFieldValue;

				// Add Form Field
				nodes.push(
					<Form.Item
						className="data-item-form-item"
						key={uniField.name}
						validateStatus={(error ? "error" : undefined)}
						help={error || undefined}
					>

						{this.props.form.getFieldDecorator(`${uniField.name}`, {
							rules: (
								this.props.isRequired
									?
									[{ required: true, message: `'${listValue.template.field.name}' is required` }]
									:
									[]
							),
							initialValue: (listValue.value !== undefined ? listValue.value.id : undefined)
						})(

							<Select
								showAction={["focus", "click"]}
								placeholder={`Select A(n) ${listValue.template.field.name}`}
								showSearch={true}
								filterOption={this.filterListOptions}
							>
								{(listValue.template.field as ListDataField).options.map((option, index) => (

									<Select.Option
										key={index}
										value={option.id}
									>
										{option.value}
									</Select.Option>
								))}
							</Select>
						)}

					</Form.Item>
				);

				break;

			case TextDataFieldValue.$type:

				// Add Form Field
				nodes.push(
					<Form.Item
						className="data-item-form-item"
						key={uniField.name}
						validateStatus={(error ? "error" : undefined)}
						help={error || undefined}
					>

						{this.props.form.getFieldDecorator(`${uniField.name}`, {
							rules: (
								this.props.isRequired
									?
									[{ required: true, message: `'${uniField.value.template.field.name}' is required` }]
									:
									[]
							),
							initialValue: (uniField.value as TextDataFieldValue).value
						})(

							<Input.TextArea
								placeholder={uniField.value.template.field.name}
								autosize={{ minRows: 4, maxRows: 6 }}
							/>
						)}

					</Form.Item>
				);

				break;
		}

		// // Check For Link
		// if (LinkDataField.isLinkField(uniField.value.template.field) && (uniField.value.template.field as LinkDataField).hasLink) {

		// 	// Cast Field
		// 	const linkField = uniField.value.template.field as LinkDataField;
		// 	const linkValue = uniField.value as LinkDataFieldValue;

		// 	// Set Field Error
		// 	const linkDestinationError = this.props.form.isFieldTouched("link-destination") && this.props.form.getFieldError("link-destination");

		// 	nodes.push(...[

		// 		(
		// 			<Divider
		// 				key="link-field-divider"
		// 				orientation="left"
		// 				className="link-destination-divider"
		// 			/>
		// 		),
		// 		(
		// 			<Form.Item
		// 				className="data-item-form-item data-item-link-item"
		// 				key="link-destination"
		// 				validateStatus={(linkDestinationError ? "error" : undefined)}
		// 				help={linkDestinationError || undefined}
		// 				label="Link"
		// 			>

		// 				{this.props.form.getFieldDecorator("link-destination", {
		// 					rules: [{ required: true, message: `'Destination' is required` }],
		// 					initialValue: linkValue.destination
		// 				})(

		// 					<Input
		// 						placeholder="Protocol"
		// 						addonBefore={(
		// 							linkField.availableProtocols !== undefined && linkField.availableProtocols.length > 0
		// 								?
		// 								this.props.form.getFieldDecorator("link-protocol", {
		// 									rules: [{ required: true, message: `'Protocol' is required` }],
		// 									initialValue: linkValue.protocol ? linkValue.protocol.id : linkField.availableProtocols[0].id
		// 								})(

		// 									<Select>

		// 										{linkField.availableProtocols.map((protocol, index) => <Select.Option key={index} value={protocol.id}>{protocol.name}</Select.Option>)}

		// 									</Select>
		// 								)
		// 								:
		// 								undefined
		// 						)}
		// 					/>
		// 				)}

		// 			</Form.Item>
		// 		)
		// 	]);
		// }

		// Return Nodes
		return nodes;
	}

	private renderMultiField = (multiField: MultiField): React.ReactNode[] => {

		// Get Multi-Field
		const multiFieldDefinition = multiField as MultiField;

		// Initialize Multi-Field Nodes
		const multiFieldNodes: React.ReactNode[] = [];

		// Loop Through Field Value Requirements		
		(multiFieldDefinition.value.template.field as AggregateDataField).requirements.forEach((requirement) => {

			// Get Matching Values
			const matchingValues = multiFieldDefinition.fields.filter((value) => requirement.item.item.field.name === value.value.template.field.name);

			// Loop Through Matching Values
			matchingValues.forEach((matchingValue) => {

				// Render Matching Value
				const matchingValueNodes = this.renderField(matchingValue);

				// Check For Removable
				if (requirement.requiredness === Requiredness.OPTIONAL || (requirement.isRepeatable && matchingValues.length > 1)) {

					// Add Nodes To Multi-Field Nodes
					multiFieldNodes.push(
						<div
							className="removable-field"
							key={`${matchingValue.name}`}
						>

							{matchingValueNodes}

							<Tooltip title="Remove">

								<Button
									className="remove-field-button"
									icon="minus-circle"
									onClick={curry(this.removeField)(__, multiFieldDefinition, requirement, matchingValue, matchingValues)}
								/>

							</Tooltip>
						</div>
					);
				}
				else {

					// Add Matching Value Nodes To Multi-Field Nodes
					multiFieldNodes.push(...matchingValueNodes);
				}
			});

			// Check For Matching Values
			if (requirement.isRepeatable
				|| (matchingValues.length === 0
					&& requirement.requiredness === Requiredness.OPTIONAL)) {

				// Add Add Button To Multi-Field Nodes
				multiFieldNodes.push(
					<Button
						key={`${Guid.create()}`}
						className="add-field-button"
						icon="plus"
						onClick={curry(this.addField)(__, multiFieldDefinition, requirement, matchingValues)}
					>

						{`Add ${requirement.item.item.field.name}`}
					</Button>
				);
			}
		});
		

		// Add Multi-Field Nodes To Nodes
		return [
			(
				<div
					className="multi-item-field"
					key={`${multiFieldDefinition.name}`}
				>

					{multiFieldNodes}
				</div>
			)
		];
	}

	private initialize = (initializingTemplate: DataFieldTemplate, initializingValue?: DataFieldValue) => {

		// Initialize Value
		let value: DataFieldValue | undefined;

		// Check For Passed Value
		if (initializingValue) {

			// Deep Copy Value
			value = cloneDeep(initializingValue);
		}
		else {

			// Construct Value
			value = this.constructValue(initializingTemplate);
		}

		// Check Value
		if (value) {

			// Store Value
			this.value = value;

			// Check If Field & Value's Field Are The Same Type
			if (initializingTemplate.field.$type === value.template.field.$type) {

				// Initialize Field & Add To Field List
				this.fields.push(this.initializeField(initializingTemplate, value));
			}
		}
	}

	private initializeField = (template: DataFieldTemplate, value: DataFieldValue): UniField | MultiField => {

		// Switch On Field Type
		switch (template.field.$type) {

			case AggregateDataField.$type:				

				// Initialize Multi-Field
				return this.initializeMultiField(template, value as AggregateDataFieldValue);

			default:

				// Initialize Uni-Field
				return this.initializeUniField(value);
		}
	}

	private initializeUniField = (dataFieldValue: DataFieldValue): UniField => {

		// Populate Field
		return new UniField(dataFieldValue);
	}

	private initializeMultiField = (template: DataFieldTemplate, aggregateDataFieldValue: AggregateDataFieldValue): MultiField => {

		// Initialize Multi-Field
		const multiField: MultiField = new MultiField(aggregateDataFieldValue);

		// Get Requirements
		const requirements = (template.field as AggregateDataField).requirements;		

		// Check For Values
		if (aggregateDataFieldValue.values === undefined) {

			// Initialize Values
			aggregateDataFieldValue.values = [];
		}
		
		// Loop Through Requirements
		requirements.sort(SingleItemRequirement.sort).forEach((requirement) => {

		// Look For Matching Value
		const matchingValues = aggregateDataFieldValue.values.filter((value) => value.item.template.field.name === requirement.item.item.field.name);

		// Check For Matching Values
		if (matchingValues.length > 0) {

			if (requirement.isRepeatable) {

				// Loop Through Matching Values
				matchingValues.forEach((matchingValue) => {

						//// Wrong place in code, but, when trying to incorporate additional fields (not just a single value field), such as links, try using the same DataFieldValue reference
						//// with a new field definition name (ie. two (or multiple) field definitions for the same DataFieldValue using different names).

						// Initialize Field And Add To Multi-Field
						multiField.fields.push(this.initializeField(requirement.item.item, matchingValue.item));
					});
				}
				else {

					// Initialize Field And Add To Multi-Field
					multiField.fields.push(this.initializeField(requirement.item.item, matchingValues[0].item));
				}
			}
			else if (requirement.requiredness !== Requiredness.OPTIONAL) {

				// Construct New Value
				const newValue = this.constructValue(requirement.item.item);

				// Check Constructed Value
				if (newValue !== undefined) {

					// Construct New Ordered Value
					const newOrderedValue = new OrderedDataFieldValue();
					newOrderedValue.order = requirement.item.initialOrder;
					newOrderedValue.item = newValue;

					// Add New Child Value To Value
					aggregateDataFieldValue.values.push(newOrderedValue);

					// Initialize Field And Add To Multi-Field
					multiField.fields.push(this.initializeField(requirement.item.item, newValue));
				}
			}
		});					

		// Return Multi-Field
		return multiField;
	}

	private constructValue = (template: DataFieldTemplate): DataFieldValue | undefined => {

		// Switch On Item Field Type
		switch (template.field.$type) {

			case StringDataField.$type:

				// Set Item Value
				return this.constructString(template);

			case ImageDataField.$type:

				// Set Item Value
				return this.constructImage(template);

			case ListDataField.$type:

				// Set Item Value
				return this.constructList(template);

			case AggregateDataField.$type:

				// Set Item Value
				return this.constructAggregate(template);

			case TextDataField.$type:

				// Set Item Value
				return this.constructText(template);

			default:

				// Return Nothing
				return undefined;
		}
	}

	private constructString = (template: DataFieldTemplate): StringDataFieldValue => {

		// Initialize String Value
		const stringItemValue = new StringDataFieldValue();
		stringItemValue.template = template;

		// Return String Value
		return stringItemValue;
	}

	private constructImage = (template: DataFieldTemplate): ImageDataFieldValue => {

		// Initialize Image Value
		const imageItemValue = new ImageDataFieldValue();
		imageItemValue.template = template;

		// Return Image Value
		return imageItemValue;
	}

	private constructList = (template: DataFieldTemplate): ListDataFieldValue => {

		// Initialize List Value
		const listItemValue = new ListDataFieldValue();
		listItemValue.template = template;

		// Get Default Option
		const defaultListOption = (template.field as ListDataField).defaultOption;

		// Check For Default Option
		if (defaultListOption) {

			// Set Current Value To Default Option
			listItemValue.value = defaultListOption;
		}

		// Return List Value
		return listItemValue;
	}

	private constructAggregate = (template: DataFieldTemplate): AggregateDataFieldValue => {

		// Initialize New Value
		const aggregateDataFieldValue = new AggregateDataFieldValue();
		aggregateDataFieldValue.template = template;
		aggregateDataFieldValue.values = [];

		// Loop Through Value Requirements
		for (const requirement of (template.field as AggregateDataField).requirements) {

			// Check Requiredness
			if (requirement.requiredness === Requiredness.REQUIRED) {

				// Initialize Ordered Item Value
				const orderedItemValue = new OrderedDataFieldValue();
				orderedItemValue.order = requirement.item.initialOrder;

				// Get Item Value
				const itemValue = this.constructValue(requirement.item.item);

				// Check Item Value
				if (itemValue) {

					// Set Item Value
					orderedItemValue.item = itemValue;

					// Add Item Value To Values
					aggregateDataFieldValue.values.push(orderedItemValue);
				}
			}
		}

		// Return Aggregate Value
		return aggregateDataFieldValue;
	}

	private constructText = (template: DataFieldTemplate): TextDataFieldValue => {

		// Initialize Text Value
		const textItemValue = new TextDataFieldValue();
		textItemValue.template = template;

		// Return Text Value
		return textItemValue;
	}

	private addField = (
		_event: React.MouseEvent,
		parentField: MultiField,
		requirement: SingleItemRequirement<DataFieldTemplate>,
		allMatchingFields: Array<UniField | MultiField>
	) => {

		// Ensure Field Can Be Removed
		if (requirement.isRepeatable
			|| (requirement.requiredness === Requiredness.OPTIONAL
				&& allMatchingFields.length === 0)) {

			// Initialie New Ordered Value
			const newOrderedValue = new OrderedDataFieldValue();
			newOrderedValue.order = requirement.item.initialOrder + allMatchingFields.length;

			// Construct New Value
			const newValue = this.constructValue(requirement.item.item);

			// Check New Value
			if (newValue) {

				// Add New Value To New Ordered Value
				newOrderedValue.item = newValue;

				// Increment Orders Of Existing Values
				parentField.value.values.filter((existingValue) => existingValue.order && newOrderedValue.order && existingValue.order >= newOrderedValue.order)
					.sort(OrderedDataFieldValue.sort)
					.forEach((existingValue) => existingValue.order!++);

				// Add New Ordered Value To Parent & Sort Values
				parentField.value.values.push(newOrderedValue);

				// Add New Field To Parent
				parentField.fields.push(this.initializeField(requirement.item.item, newValue));

				// Re-Render
				this.forceUpdate();
			}
		}
	}

	private removeField = (
		_event: React.MouseEvent,
		parentField: MultiField,
		requirement: SingleItemRequirement<DataFieldTemplate>,
		field: UniField | MultiField,
		allMatchingFields: Array<UniField | MultiField>
	) => {

		// Ensure Field Can Be Removed
		if (requirement.requiredness === Requiredness.OPTIONAL
			|| (requirement.isRepeatable && allMatchingFields.length > 1)) {

			// Locate Value In Parent
			const valueIndex = parentField.value.values.findIndex((existingValue) => existingValue.item === field.value);
			const fieldIndex = parentField.fields.findIndex((existingField) => existingField.name === field.name);

			// Check Value
			if (valueIndex !== -1 && fieldIndex !== -1) {

				// Store Old Order
				const oldOrder = parentField.value.values[valueIndex].order;

				// Increment Orders Of Existing Values
				parentField.value.values.filter((existingValue) => existingValue.order && oldOrder && existingValue.order >= oldOrder)
					.sort(OrderedDataFieldValue.sort)
					.forEach((existingValue) => existingValue.order!--);

				// Remove Value From Parent
				parentField.value.values.splice(valueIndex, 1);

				// Remove Field From Parent
				parentField.fields.splice(fieldIndex, 1);

				// Re-Render
				this.forceUpdate();
			}
		}
	}

	private checkValidation = () => {

		// Get Any/All Errors
		const errors = this.props.form.getFieldsError();

		// Check For Form Validation Handler
		if (this.props.onValidated) {

			// Initialize Valid Flag
			let isValid: boolean = true;

			// Check Current Form For Errors
			Object.values(errors).forEach((i) => {

				// Check For Error
				if (i !== undefined) {

					// Flag As Invalid
					isValid = false;
				}
			});

			// Fire Form Validation Handler
			this.props.onValidated(isValid);
		}
	}

	private flattenFields = (array: Array<UniField | MultiField>): UniField[] => {

		// Initialize Output Array
		const output: UniField[] = [];

		// Loop Through Array
		for (const field of array) {

			// Check For Multi-Field
			if (field instanceof UniField) {

				// Add Field To Output
				output.push(field);
			}
			else if (field instanceof MultiField) {

				// Recurse Over Multi-Field's Fields
				output.push(...this.flattenFields(field.fields));
			}
		}

		// Return Output
		return output;
	}

	private filterListOptions = (inputValue: string, option: React.ReactElement<OptionProps>): boolean => {

		// Cast Option Inner HTML To String
		// (This Needs To Be Cast To Whatever Is Used As A Child Of The Option -- In Our Case, A String)
		const optionLabel = option.props.children as string;

		// Return Whether Option Is Included
		return optionLabel.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase());
	}

	private processFieldMask = (maskString?: string): Array<string | RegExp> | false => {

		// Check Mask String
		if (maskString === undefined) {

			// Return Nothing
			return false;
		}

		// Initialize Mask Array
		const maskArray: Array<string | RegExp> = [];

		// Parse Mask String
		const mask: string[] = JSON.parse(maskString);

		// Loop Through Mask Characters
		for (const maskCharacter of mask) {

			// Check Mask Character For Regular Expression
			if (maskCharacter.startsWith("/") && maskCharacter.endsWith("/") && maskCharacter.length > 1) {

				// Convert To Regular Expression
				const maskCharacterExpression = new RegExp(maskCharacter.substring(1, maskCharacter.length - 1));

				// Add Regular Expression To Mask Array
				maskArray.push(maskCharacterExpression);
			}
			else {

				// Add String To Mask Array
				maskArray.push(maskCharacter);
			}
		}

		// Return Mask Array
		return maskArray;
	}

	private onLinkFieldLabelCollapse = (key: string | string[]) => {
		console.log(key);
		this.setState({ isLinkFieldLabelCollapsed: key.length <= 0 });
	}
}

export const DataItemForm = Form.create<IDataItemFormProps & FormComponentProps>(
	{
		name: "data_item_form"
	}
)(DataItemFormDefinition);
